import { AuthProvider } from 'react-admin';
import axiosApiInstance from '../api/axiosConfig';
import { AuthUserType, UserRole } from './types';
import { jwtDecode } from 'jwt-decode';
import { useNotification } from 'src/utils/notification';
import { getStoredEduCenter, restoreSelectedEduCenter, storeSelectedEduCenter } from './educationCenter';
import { updateEmployeeCenters } from 'src/api/helpers';

const AUTH_USER_KEY = 'auth_user';
const ACTIVITY_TIME = 'activity_time';

export function getAuthFromLocalStorage(): AuthUserType {
    return JSON.parse(localStorage.getItem(AUTH_USER_KEY) || 'null');
}

export function updateAuthInLocalStorage(auth: AuthUserType | null): void {
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(auth));
    localStorage.setItem(ACTIVITY_TIME, Date.now().toString());
}

export function clearAuthInLocalStorage(): void {
    useNotification().clearLocalStorage();
    localStorage.removeItem(AUTH_USER_KEY);
}

export function decodeToken(): { exp: number; iat: number; sub: string; educationCenterId?: number } {
    const token = getAuthFromLocalStorage()?.access_token;
    return jwtDecode(token);
}
export function checkSession(): boolean {
    const activityTime = localStorage.getItem(ACTIVITY_TIME);
    if (!activityTime) return true;

    const elapsedMs = Date.now() - parseInt(activityTime, 10);
    const oneHourMs = 60 * 60 * 1000;

    return elapsedMs < oneHourMs ? true : false;
}

const authProvider: AuthProvider = {
    // called when the user attempts to log in
    login: async ({ taxNumber, password, authDto }: { taxNumber: number; password: string; authDto: AuthUserType }) => {
        // TODO update for diia auth
        if (authDto) {
            updateAuthInLocalStorage(authDto);
            return Promise.resolve(authDto);
        }
        return await axiosApiInstance
            .post('/fakeAuth/authenticate', { taxNumber, password })
            .then((response) => {
                const data: AuthUserType = response?.data;

                const mappedEmployeeCenters = updateEmployeeCenters(response?.data?.profile?.employees);
                const updatedProfile = {
                    ...data.profile,
                    employees: mappedEmployeeCenters || data.profile.employees
                };
                updateAuthInLocalStorage({ ...data, profile: updatedProfile });
                return data.profile.id;
            })
            .then((profileId: number) => {
                const selectedEduCenterToRestore = getStoredEduCenter(profileId);
                selectedEduCenterToRestore && authProvider.changeCenter(selectedEduCenterToRestore);
            });
    },
    // called when the user clicks on the logout button
    logout: async () => {
        clearAuthInLocalStorage();
    },
    // called when the API returns an error
    checkError: (error) => {
        if (error?.status === 401 || error?.status === 403) {
            return Promise.reject();
        }
        return Promise.resolve();
    },
    // called when the user navigates to a new location, to check for authentication
    checkAuth: () => {
        const isSessionOk = checkSession();
        if (isSessionOk && getAuthFromLocalStorage()) {
            localStorage.setItem(ACTIVITY_TIME, Date.now().toString());
            return Promise.resolve();
        } else {
            return Promise.reject();
        }
    },
    getIdentity: () => {
        const profile = getAuthFromLocalStorage()?.profile;
        return profile ? Promise.resolve(profile) : Promise.reject();
    },
    // called when the user navigates to a new location, to check for permissions / roles
    getPermissions: async () => {
        return getAuthFromLocalStorage()?.profile?.authorities;
    },
    containsRoles: (roles: UserRole | UserRole[], oneOf = true): boolean => {
        const profile = getAuthFromLocalStorage()?.profile;
        return containsUserRoles(profile?.authorities || [], roles, oneOf);
    },
    changeCenter: async (educationCenter: number, afterChangeUrl?: string) => {
        return await axiosApiInstance
            .get('/auth/change-center', {
                headers: {
                    'Education-Center': educationCenter
                }
            })
            .then((response) => {
                updateAuthInLocalStorage(response.data);
                afterChangeUrl && storeSelectedEduCenter(educationCenter);
            })
            .then(() => {
                if (afterChangeUrl) {
                    window.location.href = afterChangeUrl;
                } else {
                    window.location.reload();
                }
            });
    },
    getEducationCenterId: (): number | undefined => {
        // get last selected education center from non-deletable localstorage item
        return restoreSelectedEduCenter() ?? decodeToken().educationCenterId;
    }
};

const AND_FUNC = (a: boolean, b: boolean) => a && b;
const OR_FUNC = (a: boolean, b: boolean) => a || b;

function containsUserRoles(userRoles: UserRole[], roles: UserRole | UserRole[], oneOf: boolean): boolean {
    const statementFunc = oneOf ? OR_FUNC : AND_FUNC;
    return (
        userRoles &&
        ((Array.isArray(roles) &&
            roles.reduce(
                (prev: boolean, current: UserRole): boolean => statementFunc(userRoles.includes(current), prev),
                !oneOf
            )) ||
            userRoles.includes(roles as UserRole))
    );
}

export default authProvider;
